import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileSignature, ClipboardCheck, MessageSquare } from "lucide-react";
import { optPts, gradeWeight, gradeChecklist } from "@/data/admission";
import { BeatShell, OptionList, ChipRow, NumberField, fmt1 } from "@/components/nutrition/NutritionParts";
import { WeighScale } from "@/components/admission/WeighScale";
import { ConsentSheet } from "@/components/admission/AdmissionInteractive";

const PatientLine = ({ c }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 leading-relaxed" data-testid="admission-briefing">
    <span className="font-semibold text-slate-900">{c.name}</span> — {c.breed}, {c.signalment}. Owner: {c.owner}.{" "}
    <span className="font-semibold text-slate-900">For: {c.procedure}.</span>
    <div className="mt-2">{c.briefing}</div>
  </div>
);

/* ── Beat 1 · Identity & procedure ────────────────────────────────────────── */
export const IdentityBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.identity.options[idx].score, 20, 10),
    max: 20,
    detail: "Right patient · right procedure",
    key: "identity",
    label: "Identity & procedure",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an action.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={ShieldCheck}
      eyebrow="Stage 1 of 5 · Identity"
      title="Identity & procedure check"
      subtitle={c.identity.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="identity-primary-btn"
    >
      <PatientLine c={c} />
      <OptionList options={c.identity.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="identity-option" />
    </BeatShell>
  );
};

/* ── Beat 2 · Consent & finances ──────────────────────────────────────────── */
export const ConsentBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [signed, setSigned] = useState(false);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.consent.options[idx].score, 20, 10),
    max: 20,
    detail: "Consent · estimate · contact",
    key: "consent",
    label: "Consent & finances",
  });

  const onPrimary = () => {
    if (!checked) {
      if (!signed) return toast.error("Complete and sign the consent form first.");
      if (idx == null) return toast.error("Choose an action.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={FileSignature}
      eyebrow="Stage 2 of 5 · Consent"
      title="Consent & finances"
      subtitle={c.consent.prompt}
      primaryLabel={checked ? "Continue →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="consent-primary-btn"
    >
      <ConsentSheet c={c} signed={signed} onSign={setSigned} />
      <OptionList options={c.consent.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="consent-option" />
    </BeatShell>
  );
};

/* ── Beat 3 · Pre-op assessment (weigh-in + safety checklist) ─────────────── */
export const PreopBeat = ({ c, onDone }) => {
  const [weight, setWeight] = useState("");
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;

  const toggle = (i) => {
    const n = new Set(sel);
    n.has(i) ? n.delete(i) : n.add(i);
    setSel(n);
  };

  const grade = () => {
    const wRes = gradeWeight(weight, c.preop.scale);
    const cl = gradeChecklist(c.preop.checklist, sel);
    return {
      wRes,
      scoreObj: {
        earned: wRes.pts + cl.pts,
        max: 30,
        detail: `Weight ${fmt1(c.preop.scale)} kg · concerns ${cl.caught}/${cl.total}, ${cl.falseTaps} false`,
        key: "preop",
        label: "Pre-op assessment",
      },
    };
  };

  const onPrimary = () => {
    if (!checked) {
      if (weight === "") return toast.error("Record the patient's weight from the scale.");
      setRes(grade());
    } else onDone(res.scoreObj);
  };

  return (
    <BeatShell
      Icon={ClipboardCheck}
      eyebrow="Stage 3 of 5 · Pre-op"
      title="Pre-op assessment"
      subtitle="Record an accurate weight and flag every genuine pre-op concern."
      primaryLabel={checked ? "Continue →" : "Check answers"}
      onPrimary={onPrimary}
      primaryTestId="preop-primary-btn"
    >
      <WeighScale target={c.preop.scale} species={c.species} />

      <NumberField
        testid="weight-input"
        label="Record weight (drives all drug doses)"
        unit="kg"
        hint="Enter the value shown on the scale"
        value={weight}
        onChange={setWeight}
        disabled={checked}
        result={res?.wRes}
        correctText={`${fmt1(c.preop.scale)} kg`}
      />

      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
          {c.preop.checklistPrompt}
        </div>
        <ChipRow chips={c.preop.checklist} selected={sel} onToggle={toggle} revealed={checked} testidPrefix="preop-chip" />
        {checked && (
          <div className="mt-3 space-y-1.5" data-testid="preop-coaching">
            {c.preop.checklist.filter((x) => x.flag).map((x, i) => (
              <div key={i} className="text-[11px] text-slate-600 leading-snug">
                <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
              </div>
            ))}
          </div>
        )}
      </div>
    </BeatShell>
  );
};

/* ── Beat 5 · Communicate & handover ──────────────────────────────────────── */
export const HandoverBeat = ({ c, onDone }) => {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const checked = !!res;

  const grade = () => ({
    earned: optPts(c.handover.options[idx].score, 15, 7),
    max: 15,
    detail: "Clinical handover · client expectations",
    key: "handover",
    label: "Communicate & handover",
  });

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an action.");
      setRes(grade());
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={MessageSquare}
      eyebrow="Stage 5 of 5 · Handover"
      title="Communicate & handover"
      subtitle={c.handover.prompt}
      primaryLabel={checked ? "See debrief →" : "Check answer"}
      onPrimary={onPrimary}
      primaryTestId="handover-primary-btn"
    >
      <OptionList options={c.handover.options} value={idx} onPick={setIdx} revealed={checked} testidPrefix="handover-option" />
    </BeatShell>
  );
};
